
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchAllProfiles, updateUserProfile } from "@/services/adminService";
import { Skeleton } from "@/components/ui/skeleton";
import { Profile } from "@/services/profiles";
import { Shield, User, UserX, UserCheck, Pencil, Save, X, Trash2, Building2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableHead, 
  TableCell 
} from "@/components/ui/table";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const AdminUsersTable = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editedCredits, setEditedCredits] = useState<number>(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<Profile | null>(null);
  const [fullDeleteDialogOpen, setFullDeleteDialogOpen] = useState(false);
  const [userToFullDelete, setUserToFullDelete] = useState<Profile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const { data: profiles, isLoading, error } = useQuery({
    queryKey: ["admin-profiles"],
    queryFn: fetchAllProfiles,
  });

  const handleToggleAdmin = async (userId: string, isCurrentlyAdmin: boolean) => {
    try {
      await updateUserProfile(userId, { is_admin: !isCurrentlyAdmin });
      toast.success(`User ${isCurrentlyAdmin ? 'removed from' : 'added to'} admin role`);
      queryClient.invalidateQueries({ queryKey: ["admin-profiles"] });
    } catch (error) {
      toast.error("Failed to update admin status");
      console.error("Error updating admin status:", error);
    }
  };

  const handleToggleUniversityOnly = async (userId: string, currentValue: boolean) => {
    try {
      await updateUserProfile(userId, { university_only_swaps: !currentValue });
      toast.success(`University-only swaps ${!currentValue ? 'enabled' : 'disabled'}`);
      queryClient.invalidateQueries({ queryKey: ["admin-profiles"] });
    } catch (error) {
      toast.error("Failed to update university-only setting");
      console.error("Error updating university-only setting:", error);
    }
  };

  const handleEditCredits = (userId: string, currentCredits: number) => {
    setEditingUser(userId);
    setEditedCredits(currentCredits);
  };

  const handleSaveCredits = async (userId: string) => {
    try {
      await updateUserProfile(userId, { credit_balance: editedCredits });
      toast.success("Credits updated successfully");
      queryClient.invalidateQueries({ queryKey: ["admin-profiles"] });
      setEditingUser(null);
    } catch (error) {
      toast.error("Failed to update credits");
      console.error("Error updating credits:", error);
    }
  };

  const openDeleteDialog = (user: Profile) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await updateUserProfile(userToDelete.id, { is_deleted: true });
      toast.success(`User ${userToDelete.username || userToDelete.id} has been suspended`);
      queryClient.invalidateQueries({ queryKey: ["admin-profiles"] });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error("Failed to suspend user");
      console.error("Error suspending user:", error);
    }
  };

  const handleCancelCreditEdit = () => {
    setEditingUser(null);
  };

  const openFullDeleteDialog = (user: Profile) => {
    setUserToFullDelete(user);
    setFullDeleteDialogOpen(true);
  };

  const handleFullDeleteUser = async () => {
    if (!userToFullDelete) return;
    
    setIsDeleting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      
      if (!token) {
        toast.error("You must be logged in to perform this action");
        return;
      }

      const response = await supabase.functions.invoke("delete-user", {
        body: { userId: userToFullDelete.id },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      toast.success(`User ${userToFullDelete.display_username || userToFullDelete.username || userToFullDelete.id} has been permanently deleted`);
      queryClient.invalidateQueries({ queryKey: ["admin-profiles"] });
      setFullDeleteDialogOpen(false);
      setUserToFullDelete(null);
    } catch (error) {
      console.error("Error fully deleting user:", error);
      toast.error(`Failed to delete user: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRowClick = (profileId: string, event: React.MouseEvent) => {
    // Don't navigate if clicking on buttons or inputs
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('input')) {
      return;
    }
    navigate(`/community/profile/${profileId}`);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-500">Error loading users: {error.toString()}</p>
      </div>
    );
  }

  if (!profiles || profiles.length === 0) {
    return <p className="text-center p-4">No users found</p>;
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Lab/University</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Credits</TableHead>
            <TableHead>Uni Only</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Joined</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {profiles.map((profile) => (
            <TableRow 
              key={profile.id} 
              className={`${profile.is_deleted ? "bg-red-50" : ""} cursor-pointer hover:bg-muted/50`}
              onClick={(event) => handleRowClick(profile.id, event)}
            >
              <TableCell>
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{profile.display_username || profile.username || 'No username'}</p>
                    <p className="text-xs text-muted-foreground">{profile.id.substring(0, 8)}...</p>
                  </div>
                </div>
              </TableCell>
              <TableCell>{profile.username || 'No email'}</TableCell>
              <TableCell>
                <p>{profile.lab || 'N/A'}</p>
                <p className="text-xs text-muted-foreground">{profile.university || 'N/A'}</p>
              </TableCell>
              <TableCell>
                {profile.city && profile.country ? (
                  <p>{`${profile.city}, ${profile.country}`}</p>
                ) : (
                  <p>{profile.city || profile.country || 'N/A'}</p>
                )}
              </TableCell>
              <TableCell>
                {editingUser === profile.id ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={editedCredits}
                      onChange={(e) => setEditedCredits(Number(e.target.value))}
                      className="w-20 h-8"
                    />
                    <Button size="icon" variant="ghost" onClick={() => handleSaveCredits(profile.id)}>
                      <Save className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={handleCancelCreditEdit}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span>{profile.credit_balance}</span>
                    <Button size="icon" variant="ghost" onClick={() => handleEditCredits(profile.id, profile.credit_balance)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!!profile.university_only_swaps}
                    onCheckedChange={() => handleToggleUniversityOnly(profile.id, !!profile.university_only_swaps)}
                    disabled={!profile.university_id}
                  />
                  {profile.university_id && (
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {profile.is_admin ? (
                    <>
                      <Shield className="h-4 w-4 text-primary" />
                      <span>Admin</span>
                    </>
                  ) : (
                    <span>User</span>
                  )}
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleToggleAdmin(profile.id, !!profile.is_admin)}
                  >
                    {profile.is_admin ? 'Demote' : 'Promote'}
                  </Button>
                </div>
              </TableCell>
              <TableCell>
                {profile.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}
              </TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant={profile.is_deleted ? "outline" : "destructive"} 
                    onClick={() => {
                      if (profile.is_deleted) {
                        updateUserProfile(profile.id, { is_deleted: false })
                          .then(() => {
                            toast.success(`User ${profile.display_username || profile.username || profile.id} has been reactivated`);
                            queryClient.invalidateQueries({ queryKey: ["admin-profiles"] });
                          })
                          .catch((err) => {
                            toast.error("Failed to reactivate user");
                            console.error(err);
                          });
                      } else {
                        openDeleteDialog(profile);
                      }
                    }}
                  >
                    {profile.is_deleted ? (
                      <>
                        <UserCheck className="h-4 w-4 mr-1" />
                        Reactivate
                      </>
                    ) : (
                      <>
                        <UserX className="h-4 w-4 mr-1" />
                        Suspend
                      </>
                    )}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => openFullDeleteDialog(profile)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend User Account</DialogTitle>
            <DialogDescription>
              Are you sure you want to suspend {userToDelete?.display_username || userToDelete?.username || userToDelete?.id}? 
              This will prevent them from logging in or using the system.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser}>
              <UserX className="h-4 w-4 mr-2" />
              Suspend User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={fullDeleteDialogOpen} onOpenChange={setFullDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Permanently Delete User</DialogTitle>
            <DialogDescription className="space-y-2">
              <p>
                Are you sure you want to <strong>permanently delete</strong> {userToFullDelete?.display_username || userToFullDelete?.username || userToFullDelete?.id}?
              </p>
              <p className="text-destructive font-medium">
                This action cannot be undone. The user will be removed from both the database AND Supabase Auth. 
                They will be able to register again with the same email address.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFullDeleteDialogOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleFullDeleteUser} disabled={isDeleting}>
              <Trash2 className="h-4 w-4 mr-2" />
              {isDeleting ? "Deleting..." : "Permanently Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersTable;
